import { FiCopy } from "react-icons/fi";
import Modal from "newui/components/ui/Modal/Modal";
import type { ReviewClarification } from "../../../../types/clarification-review.type";
import { TriageStatusBadge } from "./TriageStatusBadge";

interface ApplyToAllModalProps {
	clarification: ReviewClarification | null;
	isLoading: boolean;
	isOpen: boolean;
	supplierCount: number;
	onClose: () => void;
	onConfirm: (clarificationId: number) => void;
}

export const ApplyToAllModal: React.FC<ApplyToAllModalProps> = ({
	isOpen,
	clarification,
	supplierCount,
	onClose,
	onConfirm,
	isLoading,
}) => {
	if (!clarification) {
		return null;
	}

	const handleConfirm = () => {
		onConfirm(clarification.id);
	};

	const truncateText = (text: string, maxLength: number): string => {
		if (text.length <= maxLength) {
			return text;
		}
		return `${text.substring(0, maxLength)}...`;
	};

	return (
		<Modal
			isOpen={isOpen}
			onClose={onClose}
			size="lg"
			title="Apply Clarification to All Suppliers"
			description={`This will create copies of clarification #${clarification.reference_number} for all other suppliers.`}
			modalType="standard"
			iconType="info"
			closeOnEsc
			closeOnOverlayClick={!isLoading}
			primaryButton={{
				onClick: handleConfirm,
				text: isLoading ? "Applying..." : "Confirm & Apply to All",
				size: "sm",
				width: "full",
				disabled: isLoading,
			}}
			secondaryButton={{
				onClick: onClose,
				text: "Cancel",
				size: "sm",
				width: "full",
			}}
		>
			<div className="space-y-4" data-testid="apply-to-all-modal">
				{/* Warning banner */}
				<div className="rounded-lg border border-amber-200 bg-amber-50 p-3">
					<p className="text-sm text-amber-800">
						This will duplicate the clarification to <span className="font-semibold">{supplierCount} other supplier{supplierCount !== 1 ? "s" : ""}</span>.
						Each copy will be linked back to this original and inherit the same triage status.
					</p>
				</div>

				{/* Clarification summary */}
				<div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
					<div className="mb-2 flex items-center gap-2">
						<span className="text-sm font-medium text-gray-700">
							#{clarification.reference_number}
						</span>
						<TriageStatusBadge status={clarification.triage_status} />
					</div>
					<p className="text-sm text-gray-600">
						{truncateText(clarification.clarification_text, 200)}
					</p>
					<div className="mt-2 flex items-center gap-3 text-xs text-gray-400">
						<span>Supplier: {clarification.supplier_name}</span>
						<span>Relates to: {clarification.relates_to || "—"}</span>
					</div>
				</div>

				{/* What will happen */}
				<div className="rounded-lg border border-blue-200 bg-blue-50 p-3">
					<div className="flex items-start gap-2">
						<FiCopy className="mt-0.5 shrink-0 text-blue-600" size={14} />
						<div className="text-sm text-blue-800">
							<p className="font-medium">What happens next:</p>
							<ul className="mt-1 list-inside list-disc space-y-0.5">
								<li>A copy is created for each of the {supplierCount} other supplier{supplierCount !== 1 ? "s" : ""}</li>
								<li>Each copy links back to this clarification as the source</li>
								<li>Copies inherit the current triage status and edited text</li>
							</ul>
						</div>
					</div>
				</div>
			</div>
		</Modal>
	);
};
